import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@ownyou/ui-components';
import type { AuditActorKind } from '@ownyou/shared-types';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { useAudit } from '../contexts/AuditContext';
import {
  readAuditLog,
  type AuditLogReadModel,
  type AuditLogRow,
} from '../lib/audit';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; model: AuditLogReadModel }
  | { status: 'error'; error: string };

interface FilterState {
  since: string;
  until: string;
  actor: '' | AuditActorKind;
  action: string;
  targetKind: string;
}

const DEFAULT_FILTERS: FilterState = {
  since: '',
  until: '',
  actor: '',
  action: '',
  targetKind: '',
};

const PAGE_SIZE = 25;

export function AuditLog() {
  const navigate = useNavigate();
  const { store, isReady: storeReady } = useStore();
  const { wallet, isAuthenticated } = useAuth();
  const { deviceId, keyStore } = useAudit();
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const userId = wallet?.address ?? '';

  const load = useCallback(async (nextCursor: string | null, append: boolean) => {
    if (!store || !storeReady || !isAuthenticated || !userId) {
      setState({ status: 'ready', model: emptyAuditLogModel(userId) });
      return;
    }

    setState((previous) => append && previous.status === 'ready' ? previous : { status: 'loading' });
    try {
      const model = await readAuditLog({
        store,
        userId,
        keyStore,
        currentDeviceId: deviceId,
        query: {
          cursor: nextCursor,
          limit: PAGE_SIZE,
          since: filters.since ? new Date(filters.since).getTime() : undefined,
          until: filters.until ? new Date(filters.until).getTime() : undefined,
          actor: filters.actor || undefined,
          action: filters.action.trim() || undefined,
          targetKind: filters.targetKind.trim() || undefined,
        },
      });
      setState((previous) => {
        if (!append || previous.status !== 'ready') return { status: 'ready', model };
        return {
          status: 'ready',
          model: {
            ...model,
            rows: [...previous.model.rows, ...model.rows],
          },
        };
      });
      setCursor(model.nextCursor);
    } catch (error) {
      setState({
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to load AuditLog',
      });
    }
  }, [deviceId, filters, isAuthenticated, keyStore, store, storeReady, userId]);

  useEffect(() => {
    setCursor(null);
    void load(null, false);
  }, [load]);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="audit-log-screen" className="min-h-screen bg-neutral-50">
        <Header showLogo={false} title="AuditLog" onBack={() => navigate(-1)} showFilters={false} />
        <main data-testid="audit-log-list" className="mx-auto max-w-5xl px-4 py-6">
          <section className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-600">
            Connect your wallet to view current-device Audit events.
          </section>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-screen" className="min-h-screen bg-neutral-50">
      <Header showLogo={false} title="AuditLog" onBack={() => navigate(-1)} showFilters={false} />
      <main data-testid="audit-log-list" className="mx-auto max-w-6xl px-4 py-5">
        <section className="mb-4 grid gap-3 rounded-lg border border-neutral-200 bg-white p-4 md:grid-cols-5">
          <label className="text-xs font-medium text-neutral-600">
            Since
            <input
              className="mt-1 w-full rounded-md border border-neutral-200 px-2 py-2 text-sm text-neutral-900"
              type="datetime-local"
              value={filters.since}
              onChange={(event) => updateFilter('since', event.target.value)}
            />
          </label>
          <label className="text-xs font-medium text-neutral-600">
            Until
            <input
              className="mt-1 w-full rounded-md border border-neutral-200 px-2 py-2 text-sm text-neutral-900"
              type="datetime-local"
              value={filters.until}
              onChange={(event) => updateFilter('until', event.target.value)}
            />
          </label>
          <label className="text-xs font-medium text-neutral-600">
            Actor
            <select
              className="mt-1 w-full rounded-md border border-neutral-200 px-2 py-2 text-sm text-neutral-900"
              value={filters.actor}
              onChange={(event) => updateFilter('actor', event.target.value as FilterState['actor'])}
            >
              <option value="">All</option>
              <option value="user">User</option>
              <option value="agent">Agent</option>
              <option value="system">System</option>
            </select>
          </label>
          <label className="text-xs font-medium text-neutral-600">
            Action
            <input
              className="mt-1 w-full rounded-md border border-neutral-200 px-2 py-2 text-sm text-neutral-900"
              type="search"
              value={filters.action}
              onChange={(event) => updateFilter('action', event.target.value)}
              placeholder="gdpr.export_requested"
            />
          </label>
          <label className="text-xs font-medium text-neutral-600">
            Target
            <input
              className="mt-1 w-full rounded-md border border-neutral-200 px-2 py-2 text-sm text-neutral-900"
              type="search"
              value={filters.targetKind}
              onChange={(event) => updateFilter('targetKind', event.target.value)}
              placeholder="llm_route"
            />
          </label>
        </section>

        {state.status === 'loading' && (
          <section className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
            Loading AuditLog...
          </section>
        )}

        {state.status === 'error' && (
          <section className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            {state.error}
          </section>
        )}

        {state.status === 'ready' && state.model.rows.length === 0 && (
          <section className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-600">
            No current-device Audit events match these filters.
          </section>
        )}

        {state.status === 'ready' && state.model.rows.length > 0 && (
          <section className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <div className="border-b border-neutral-200 px-4 py-3 text-sm text-neutral-600">
              {state.model.totalMatched} current-device events from {state.model.sourceNamespace[0]}
              {state.model.sourceTruncated && (
                <span className="ml-2 text-amber-700">
                  Source read truncated at {state.model.sourceReadLimit} events.
                </span>
              )}
            </div>
            <div className="divide-y divide-neutral-200">
              {state.model.rows.map((row) => (
                <AuditLogRowView key={row.id} row={row} />
              ))}
            </div>
            {cursor && (
              <div className="border-t border-neutral-200 p-3">
                <button
                  type="button"
                  className="rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                  onClick={() => void load(cursor, true)}
                >
                  Load more
                </button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function AuditLogRowView({ row }: { row: AuditLogRow }) {
  const [open, setOpen] = useState(false);
  const state = verificationState(row);

  return (
    <article data-testid="audit-log-row" data-verification-state={row.verificationState} className="p-4">
      <button
        type="button"
        className="grid w-full gap-3 text-left md:grid-cols-[11rem_1fr_10rem]"
        onClick={() => setOpen((value) => !value)}
      >
        <time className="text-sm text-neutral-500">{new Date(row.ts).toLocaleString()}</time>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-neutral-950">{row.action}</span>
          <span className="block truncate text-sm text-neutral-600">
            {row.actor.kind}{row.actor.id ? `:${shorten(row.actor.id)}` : ''}{' -> '}{row.target.kind}
            {row.target.stableRef ? `:${row.target.stableRef}` : ''}
          </span>
        </span>
        <span className={`rounded-md px-2 py-1 text-xs font-medium ${state.className}`}>
          {state.label}
        </span>
      </button>
      {open && (
        <dl className="mt-3 grid gap-2 rounded-md bg-neutral-50 p-3 text-xs text-neutral-700 md:grid-cols-2">
          <Detail label="Device" value={row.deviceId} />
          <Detail label="Audit key" value={row.auditKeyId} />
          <Detail label="Delegation" value={row.delegationId} />
          <Detail label="Result" value={row.result ?? 'not recorded'} />
          {row.routeId && <Detail label="Route" value={row.routeId} />}
          {row.traceId && <Detail label="Trace" value={row.traceId} />}
          {row.reason && <Detail label="Reason" value={row.reason} />}
          {row.verificationErrors.length > 0 && (
            <Detail label="Verification" value={row.verificationErrors.join('; ')} />
          )}
        </dl>
      )}
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-medium text-neutral-500">{label}</dt>
      <dd className="break-words text-neutral-900">{value}</dd>
    </div>
  );
}

function verificationState(row: AuditLogRow): { label: string; className: string } {
  switch (row.verificationState) {
    case 'verified':
      return { label: 'Verified', className: 'bg-emerald-50 text-emerald-700' };
    case 'invalid_signature':
      return { label: 'Invalid signature', className: 'bg-red-50 text-red-700' };
    case 'missing_delegation':
      return { label: 'Missing delegation', className: 'bg-amber-50 text-amber-700' };
    case 'privacy_failure':
      return { label: 'Privacy failure', className: 'bg-red-50 text-red-700' };
    case 'schema_invalid':
      return { label: 'Schema invalid', className: 'bg-red-50 text-red-700' };
  }
}

function emptyAuditLogModel(userId: string): AuditLogReadModel {
  return {
    rows: [],
    nextCursor: null,
    totalMatched: 0,
    sourceReadCount: 0,
    sourceTruncated: false,
    sourceReadLimit: 100_000,
    sourceNamespace: ['ownyou.audit_events', userId] as const,
    scope: 'current-device',
  };
}

function shorten(value: string): string {
  return value.length <= 14 ? value : `${value.slice(0, 8)}...${value.slice(-4)}`;
}
